/* GSD-860: appointment display data for the Shopify booking handoff. */
(() => {
  if (window.SmileWhiteBookingConfirmation) return;

  const MILLISECONDS_PER_DAY = 86_400_000;
  const HANDOFF_PARAMETER = "booking_handoff";
  const HISTORY_STATE_KEY = "smileWhiteBookingHandoff";
  const DATE_PARAMETERS = [
    "appointment_start",
    "appointment_end",
    "appointment_timezone",
  ];
  const LEGACY_PARAMETERS = [
    "invitee_email",
    "invitee_uuid",
    "event_start_time",
    "event_end_time",
    "reschedule_url",
    "meeting_url",
    "event_type_name",
    "uid",
  ];
  const summaryStorageKey = (path) => `sw-booking-summary:v2:${path}`;
  let calendarDownloadUrl;

  function isValidSummary(summary) {
    if (
      !summary ||
      !Number.isFinite(summary.start) ||
      !Number.isFinite(summary.end)
    )
      return false;
    const duration = summary.end - summary.start;
    if (duration <= 0 || duration > MILLISECONDS_PER_DAY) return false;
    try {
      new Intl.DateTimeFormat("en-GB", { timeZone: summary.timezone }).format(
        summary.start,
      );
      return true;
    } catch {
      return false;
    }
  }

  function createHandoffId() {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
      "",
    );
  }

  function save(destination, summary) {
    const url = new URL(destination, location.origin);
    if (url.origin !== location.origin || !isValidSummary(summary))
      return url.href;
    const record = {
      id: createHandoffId(),
      start: summary.start,
      end: summary.end,
      timezone: summary.timezone,
      expiresAt: Date.now() + MILLISECONDS_PER_DAY,
    };
    if (typeof summary.address === "string") record.address = summary.address.slice(0, 2000);
    if (typeof summary.title === "string") record.title = summary.title.slice(0, 200);
    // This random display marker is not an appointment ID or authorisation token.
    url.searchParams.set(HANDOFF_PARAMETER, record.id);
    try {
      sessionStorage.setItem(
        summaryStorageKey(url.pathname),
        JSON.stringify(record),
      );
    } catch {
      url.searchParams.set("appointment_start", String(record.start));
      url.searchParams.set("appointment_end", String(record.end));
      url.searchParams.set("appointment_timezone", record.timezone);
    }
    return url.href;
  }

  function rememberHandoff(record) {
    const cleanUrl = new URL(location.href);
    [HANDOFF_PARAMETER, ...DATE_PARAMETERS].forEach((name) =>
      cleanUrl.searchParams.delete(name),
    );
    const state = { ...history.state, [HISTORY_STATE_KEY]: record.id };
    history.replaceState(state, "", cleanUrl.href);
  }

  function readSummary() {
    const parameters = new URLSearchParams(location.search);
    if (LEGACY_PARAMETERS.some((name) => parameters.has(name))) return null;
    const handoffId =
      parameters.get(HANDOFF_PARAMETER) || history.state?.[HISTORY_STATE_KEY];
    if (!/^[a-f0-9]{32}$/.test(handoffId || "")) return null;

    let storedSummary;
    try {
      storedSummary = JSON.parse(
        sessionStorage.getItem(summaryStorageKey(location.pathname)) || "null",
      );
    } catch {
      storedSummary = null;
    }
    const storedSummaryMatches =
      storedSummary?.id === handoffId && storedSummary.expiresAt > Date.now();
    if (storedSummaryMatches && isValidSummary(storedSummary)) {
      if (parameters.has(HANDOFF_PARAMETER)) rememberHandoff(storedSummary);
      return storedSummary;
    }
    if (
      !parameters.has(HANDOFF_PARAMETER) ||
      !parameters.has("appointment_start") ||
      !parameters.has("appointment_end")
    )
      return null;
    const fallbackSummary = {
      id: handoffId,
      start: Number(parameters.get("appointment_start")),
      end: Number(parameters.get("appointment_end")),
      timezone: parameters.get("appointment_timezone"),
      expiresAt: Date.now() + MILLISECONDS_PER_DAY,
    };
    if (!isValidSummary(fallbackSummary)) return null;
    try {
      sessionStorage.setItem(
        summaryStorageKey(location.pathname),
        JSON.stringify(fallbackSummary),
      );
      rememberHandoff(fallbackSummary);
    } catch {
      // Leave the date-only fallback in place when browser storage is unavailable.
    }
    return fallbackSummary;
  }

  function calendarTimestamp(timestamp) {
    return new Date(timestamp)
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");
  }

  function escapeCalendarText(value) {
    return String(value).replace(/\\/g, "\\\\").replace(/\r?\n/g, "\\n").replace(/[,;]/g, "\\$&");
  }

  function buildCalendarFile(summary) {
    return [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Smile White//Consultation//EN",
      "BEGIN:VEVENT",
      `UID:${summary.start}-consultation@smilewhite.co.uk`,
      `DTSTAMP:${calendarTimestamp(Date.now())}`,
      `DTSTART:${calendarTimestamp(summary.start)}`,
      `DTEND:${calendarTimestamp(summary.end)}`,
      `SUMMARY:${escapeCalendarText(summary.title || "Smile White consultation")}`,
      ...(summary.address ? [`LOCATION:${escapeCalendarText(summary.address)}`] : []),
      "END:VEVENT",
      "END:VCALENDAR",
      "",
    ].join("\r\n");
  }

  function enableCalendarDownload(control, summary) {
    if (!control) return;
    control.style.visibility = "visible";
    control.setAttribute("role", "button");
    control.tabIndex = 0;
    control.onclick = () => {
      if (calendarDownloadUrl) URL.revokeObjectURL(calendarDownloadUrl);
      calendarDownloadUrl = URL.createObjectURL(
        new Blob([buildCalendarFile(summary)], {
          type: "text/calendar;charset=utf-8",
        }),
      );
      const link = document.createElement("a");
      link.href = calendarDownloadUrl;
      link.download = "smile-white-consultation.ics";
      link.click();
    };
    // Native buttons already translate Enter/Space into one click.
    if (control.tagName !== "BUTTON") control.onkeydown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        control.onclick();
      }
    };
  }

  function formatSummary(summary) {
    return {
      day: new Intl.DateTimeFormat("en-GB", { timeZone: summary.timezone, weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(summary.start),
      time: new Intl.DateTimeFormat("en-GB", { timeZone: summary.timezone, hour: "2-digit", minute: "2-digit" }).format(summary.start),
      duration: `${Math.round((summary.end - summary.start) / 60_000)} mins · ${summary.timezone}`,
    };
  }

  // Used by the event page as well as the original consultation confirmation.
  function renderAppointment(root, summary) {
    if (!root || !isValidSummary(summary)) return false;
    const formatted = formatSummary(summary);
    const appointment = root.querySelector("[data-appointment]");
    if (!appointment) return false;
    appointment.hidden = false;
    root.querySelector("[data-appointment-date]").textContent = formatted.day;
    root.querySelector("[data-appointment-time]").textContent = `${formatted.time} · ${formatted.duration}`;
    const title = root.querySelector("[data-appointment-title]");
    if (title) {
      title.textContent = summary.title || "";
      title.hidden = !summary.title;
    }
    const address = root.querySelector("[data-booking-location]");
    if (address) {
      address.textContent = summary.address || "";
      address.hidden = !summary.address;
    }
    const calendar = root.querySelector("[data-calendar]");
    if (calendar) {
      calendar.hidden = false;
      enableCalendarDownload(calendar, summary);
    }
    return true;
  }

  function render(root) {
    if (!root) return false;
    const time = root.querySelector("[data-booking-time], .Timmer_Count");
    const day = root.querySelector(".day_timmer_info");
    const duration = root.querySelector(".hours_timmer_info");
    const timer = root.querySelector(".timmer_block_thankyou");
    if (!time || !day || !timer) return false;
    const summary = readSummary();
    if (!summary) return false;
    const formatted = formatSummary(summary);
    time.textContent = formatted.time;
    day.textContent = formatted.day;
    if (duration) duration.textContent = formatted.duration;
    const address = root.querySelector("[data-booking-location]");
    if (address) {
      address.textContent = summary.address || "";
      address.hidden = !summary.address;
    }
    const appointmentCard = root.querySelector(".appointment_card");
    const notice = root.querySelector("[data-appointment-notice]");
    if (notice) notice.hidden = true;
    [timer, time, day, appointmentCard].filter(Boolean).forEach((element) => {
      element.style.visibility = "visible";
    });
    enableCalendarDownload(root.querySelector("#calendar_trigger"), summary);
    return true;
  }

  window.SmileWhiteBookingConfirmation = Object.freeze({ save, render, readSummary, renderAppointment });
})();
